import {
  isPoweredOff,
  didIresDeviceTimeOut,
  isDryRunFinnished,
  isInWarningOrError,
  IresDevice,
  isInStandBy,
  getIresDeviceCategory,
  IresDeviceCategory,
} from "./IresDevice";

import { getClientId, getMemberClientIds } from "./../repository/ClientRepository";
import User from "./User";
import { ComBox, isDryRunActiveOrInterrupted } from "./ComBox";
import { DeviceOverview } from "./DeviceOverview";
import ClientPanel from "./ClientPanel";
import { isClientPanel } from "./DeviceSettingsManager";
import { isDeviceAvailable } from "@/utils/DeviceManagementUtils";
import { allHyDryDevicesAccess, viewHyDryDevicesOfMemberAccess } from "@/permissions/UserPermissions";
import { currentStatusAlwaysVisibleToUserClientAccess } from "@/permissions/ClientPermissions";
import { getEffectiveUserLazily } from "@/utils/EffectiveUserLazy";

export enum DeviceScope {
  ALL_DEVICES_OF_CLIENT = "ALL_DEVICES_OF_CLIENT",
  DEVICES_OF_PROJECTS_OF_USER = "DEVICES_OF_PROJECTS_OF_USER",
}

export enum GenericDeviceFilter {
  ALL_DEVICES,
  IN_USAGE,
  AVAILABLE,
  NOT_AVAILABLE
}

export enum DeviceFilter {
  ALL_DEVICES,
  IN_USAGE,
  ALL_ACTIVE_DEVICES,
  ALL_FINISHED_DEVICES,
  DEVICES_WITH_ERROR_OR_WARNING,
  OVERDUE_DEVICES,
  NOT_ASSIGNED_TO_PROJECT,
}

export function matchesGenericDeviceFilter(
  filter: GenericDeviceFilter,
  device: DeviceOverview | ClientPanel,
) {
  switch (filter) {
    case GenericDeviceFilter.ALL_DEVICES:
      return true;
    case GenericDeviceFilter.IN_USAGE:
      return isClientPanel(device) 
        ? device.currentProjectId !== null
        : device.currentlyAssigned;
    case GenericDeviceFilter.AVAILABLE:
      return isClientPanel(device) 
        ? !device.currentProjectId && isDeviceAvailable(device)
        : !device.currentlyAssigned && isDeviceAvailable(device);
    case GenericDeviceFilter.NOT_AVAILABLE:
      return !isDeviceAvailable(device);
  }
}

/**
 * The identity and capability state a device is evaluated against. It does not
 * depend on the device, only on who is asking.
 *
 * Resolving it costs several `sessionStorage` reads plus a `JSON.parse` of the whole
 * user document and of the whole client document (three times over), because
 * `getActiveUser()` and every client capability check go through
 * `SecureLocalStorage`. Evaluating a filter across a thousand devices therefore has
 * to resolve it **once** and pass it in, which is what
 * `matchesIresDeviceFilterWithContext` is for.
 */
export interface IresDeviceFilterContext {
  /** The signed-in client: its own devices are always visible. */
  clientId: string | undefined;
  currentStatusAlwaysVisible: boolean;
  canViewHyDryDevicesOfMemberClients: boolean;
  memberClientIds: Set<string>;
  /** Resolved for the user the filter is evaluated for, not for the active user. */
  seesAllHyDryDevices: boolean;
}

export function resolveIresDeviceFilterContext(
  user: User | null
): IresDeviceFilterContext {
  // The chosen user's member-client access while "Sicht auf" is active (ticket 2610052). Not `user`:
  // callers pass null on purpose for the all-devices view.
  const activeUser = getEffectiveUserLazily();

  return {
    clientId: getClientId(),
    currentStatusAlwaysVisible: currentStatusAlwaysVisibleToUserClientAccess(),
    canViewHyDryDevicesOfMemberClients: viewHyDryDevicesOfMemberAccess(activeUser),
    memberClientIds: new Set(getMemberClientIds()),
    seesAllHyDryDevices: allHyDryDevicesAccess(user),
  };
}

export function matchesIresDeviceFilterWithContext(
  filter: DeviceFilter,
  iresDevice: IresDevice,
  user: User | null,
  context: IresDeviceFilterContext,
  ignoreResponsibleUsers?: boolean,
  forcedDoesSeeAllDevices?: boolean
) {
  const assignedClientId = iresDevice.currentAssignment?.clientId;

  const isCurrentStatusVisibleToUser =
    assignedClientId === context.clientId ||
    context.currentStatusAlwaysVisible ||
    (context.canViewHyDryDevicesOfMemberClients &&
      !!assignedClientId &&
      context.memberClientIds.has(assignedClientId));

  const doesSeeAllDevices =
    forcedDoesSeeAllDevices ?? context.seesAllHyDryDevices;
  const currentDeviceAssignment = iresDevice.currentAssignment;

  const isIresDeviceAssignedToProject =
    currentDeviceAssignment &&
    !!currentDeviceAssignment.currentProjectId &&
    currentDeviceAssignment.currentProjectId !== "";

  if (
    !ignoreResponsibleUsers &&
    filter != DeviceFilter.ALL_DEVICES &&
    !doesSeeAllDevices
  ) {
    if (!currentDeviceAssignment) {
      return false;
    }

    if (
      !currentDeviceAssignment.currentProjectId ||
      currentDeviceAssignment.currentProjectId == ""
    ) {
      return false;
    }

    if (
      !user ||
      !currentDeviceAssignment.currentProjectResponsibleUserIds ||
      currentDeviceAssignment.currentProjectResponsibleUserIds.indexOf(
        user?.id
      ) < 0
    ) {
      return false;
    }
  }

  switch (filter) {
    case DeviceFilter.NOT_ASSIGNED_TO_PROJECT:
      return (
        !isIresDeviceAssignedToProject &&
        isCurrentStatusVisibleToUser &&
        !isInStandBy(iresDevice) &&
        !isPoweredOff(iresDevice) &&
        !didIresDeviceTimeOut(iresDevice)
      );
    case DeviceFilter.ALL_DEVICES:
      return true;
    case DeviceFilter.IN_USAGE: {
      let hasActiveDrying = false;

      if (getIresDeviceCategory(iresDevice) === IresDeviceCategory.COMBOX) {
        hasActiveDrying = isDryRunActiveOrInterrupted(iresDevice as ComBox);
      }

      return (
        isIresDeviceAssignedToProject ||
        hasActiveDrying ||
        (isCurrentStatusVisibleToUser && !isPoweredOff(iresDevice))
      );
    }
    case DeviceFilter.ALL_ACTIVE_DEVICES:
      return (
        isCurrentStatusVisibleToUser &&
        !isPoweredOff(iresDevice) &&
        !isInStandBy(iresDevice) &&
        !didIresDeviceTimeOut(iresDevice)
      );
    case DeviceFilter.ALL_FINISHED_DEVICES:
      return (
        !didIresDeviceTimeOut(iresDevice) &&
        isCurrentStatusVisibleToUser &&
        isDryRunFinnished(iresDevice)
      );
    case DeviceFilter.DEVICES_WITH_ERROR_OR_WARNING:
      return isCurrentStatusVisibleToUser && isInWarningOrError(iresDevice);
    case DeviceFilter.OVERDUE_DEVICES:
      return isCurrentStatusVisibleToUser && didIresDeviceTimeOut(iresDevice);
  }
}

/**
 * Resolves the filter context per call: for callers that evaluate a single device.
 * Anything evaluating a list must resolve the context once itself and call
 * `matchesIresDeviceFilterWithContext`.
 */
export function matchesIresDeviceFilter(
  filter: DeviceFilter,
  iresDevice: IresDevice,
  user: User | null,
  ignoreResponsibleUsers?: boolean,
  forcedDoesSeeAllDevices?: boolean
) {
  return matchesIresDeviceFilterWithContext(
    filter,
    iresDevice,
    user,
    resolveIresDeviceFilterContext(user),
    ignoreResponsibleUsers,
    forcedDoesSeeAllDevices
  );
}

export function getFilterName(vue: Vue, deviceFilter: DeviceFilter) {
  switch (deviceFilter) {
    case DeviceFilter.ALL_DEVICES:
      return vue.$t("panelList.filterAllDevices").toString();
    case DeviceFilter.ALL_ACTIVE_DEVICES:
      return vue.$t("panelList.filterActiveDevices").toString();
    case DeviceFilter.ALL_FINISHED_DEVICES:
      return vue.$t("panelList.filterFinishedDevices").toString();
    case DeviceFilter.DEVICES_WITH_ERROR_OR_WARNING:
      return vue.$t("panelList.filterDevicesWithWarningOrError").toString();
    case DeviceFilter.OVERDUE_DEVICES:
      return vue.$t("panelList.filterDevicesOverdueDevices").toString();
    case DeviceFilter.IN_USAGE:
      return vue.$t("panelList.filterInUsage").toString();
    case DeviceFilter.NOT_ASSIGNED_TO_PROJECT:
      return vue.$t("panelList.filterNotAssignedToProject").toString();
  }
}

export function getGenericFilterName(vue: Vue, deviceFilter: GenericDeviceFilter) {
  switch (deviceFilter) {
    case GenericDeviceFilter.ALL_DEVICES:
      return vue.$t("allDevices.filterAllDevices").toString();
    case GenericDeviceFilter.IN_USAGE:
      return vue.$t("allDevices.filterDevicesInUsage").toString();
    case GenericDeviceFilter.AVAILABLE:
      return vue.$t("allDevices.filterAvailableDevices").toString();
    case GenericDeviceFilter.NOT_AVAILABLE:
      return vue.$t("allDevices.filterUnavailableDevices").toString();
  }
}

export function getDeviceScope(
  deviceFilter: DeviceFilter,
  user: User | null | undefined
) {
  const seesAllHyDryDevices = allHyDryDevicesAccess(user ?? null);

  if (seesAllHyDryDevices) {
    return DeviceScope.ALL_DEVICES_OF_CLIENT;
  } else if (deviceFilter === DeviceFilter.ALL_DEVICES) {
    return DeviceScope.ALL_DEVICES_OF_CLIENT;
  } else {
    return DeviceScope.DEVICES_OF_PROJECTS_OF_USER;
  }
}

export function isIresDeviceAssignedToProject(iresDevice: IresDevice) {
  if (!iresDevice.currentAssignment) {
    return false;
  }

  const currentProjectId = iresDevice.currentAssignment.currentProjectId;

  return currentProjectId !== "" && !!currentProjectId;
}
